
import React, { useState, useEffect } from 'react';
import { Icons } from './Icons';

interface ConfirmationModalProps {
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
    confirmButtonText?: string;
    cancelButtonText?: string;
    isConfirming?: boolean;
    confirmationText?: string; // e.g., "delete my account"
    confirmationLabel?: string; // e.g., "To confirm, type..."
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    title,
    message,
    onConfirm,
    onCancel,
    confirmButtonText = 'Confirm',
    cancelButtonText = 'Cancel',
    isConfirming = false,
    confirmationText,
    confirmationLabel,
}) => {
    const [inputText, setInputText] = useState('');
    const [isConfirmed, setIsConfirmed] = useState(!confirmationText);

    useEffect(() => {
        if (confirmationText) {
            setIsConfirmed(inputText === confirmationText);
        }
    }, [inputText, confirmationText]);


    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm transition-all" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="fixed inset-0" onClick={onCancel} aria-hidden="true"></div>
            <div className="relative bg-white dark:bg-slate-900 rounded-[2rem] text-left overflow-hidden shadow-2xl transform transition-all sm:max-w-lg sm:w-full border border-slate-100 dark:border-slate-800">
                <div className="bg-white dark:bg-slate-900 px-8 pt-8 pb-6 sm:p-10 sm:pb-8">
                    <div className="sm:flex sm:items-start gap-6">
                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-14 w-14 rounded-2xl bg-rose-50 dark:bg-rose-900/20 sm:mx-0">
                            <Icons.AlertTriangle className="h-7 w-7 text-rose-600 dark:text-rose-400" aria-hidden="true" />
                        </div>
                        <div className="mt-4 text-center sm:mt-0 sm:text-left space-y-2">
                            <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight uppercase" id="modal-title">
                                {title}
                            </h3>
                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed">
                                {message}
                            </p>
                        </div>
                    </div>
                     {confirmationText && (
                        <div className="mt-8 space-y-3">
                            <label htmlFor="confirmation-input" className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                {confirmationLabel || `To confirm, type "${confirmationText}" below:`}
                            </label>
                            <input
                                type="text"
                                name="confirmation-input"
                                id="confirmation-input"
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                className="block w-full bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:border-indigo-500 transition-all"
                                placeholder={confirmationText}
                            />
                        </div>
                    )}
                </div>
                <div className="bg-slate-50/50 dark:bg-slate-950/50 px-8 py-6 sm:px-10 sm:flex sm:flex-row-reverse gap-3 border-t border-slate-100 dark:border-slate-800">
                    <button
                        type="button"
                        className="w-full sm:w-auto inline-flex justify-center items-center px-8 py-3 bg-rose-600 text-white text-xs font-black uppercase tracking-[0.2em] rounded-2xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-200 dark:shadow-none disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none disabled:cursor-not-allowed"
                        onClick={onConfirm}
                        disabled={!isConfirmed || isConfirming}
                    >
                        {isConfirming ? (
                            <>
                                <Icons.Loader className="w-4 h-4 mr-2 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            confirmButtonText
                        )}
                    </button>
                    <button
                        type="button"
                        className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center items-center px-8 py-3 bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-400 text-xs font-black uppercase tracking-[0.2em] rounded-2xl border border-slate-200 dark:border-slate-700 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
                        onClick={onCancel}
                        disabled={isConfirming}
                    >
                        {cancelButtonText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationModal;
